<template>
    <div>
        <v-text-field
            v-if="control_type === 'price'"
            :value="formatMoneyNumber(form_data.value)"
            outlined
            :class="inputCssClass"
            :inputmode="input_mode"
            :label="computedLabel"
            :placeholder="placeholder"
            :error-messages="form_data.value_error || errorMessages"
            @change="value_changed"
            @keypress="filterNumber"
        >
            <template v-slot:label>
                <v-tooltip bottom :disabled="!showTooltip">
                    <template v-slot:activator="{ on }">
                        <div ref="tooltipElem" class="text-field-tooltip-label" v-on="on">
                            {{ computedLabel }}
                        </div>
                    </template>
                    <span>{{ computedLabel }} </span>
                </v-tooltip>
            </template>
        </v-text-field>
        <v-text-field
            v-if="control_type === 'text'"
            v-model="form_data.value_f"
            outlined
            :class="inputCssClass"
            :inputmode="input_mode"
            :label="computedLabel"
            :placeholder="placeholder"
            :error-messages="form_data.value_error || errorMessages"
            @change="value_changed"
        >
            <template v-slot:label>
                <v-tooltip bottom :disabled="!showTooltip">
                    <template v-slot:activator="{ on }">
                        <div ref="tooltipElem" class="text-field-tooltip-label" v-on="on">
                            {{ computedLabel }}
                        </div>
                    </template>
                    <span>{{ computedLabel }} </span>
                </v-tooltip>
            </template>
        </v-text-field>
        <v-select
            v-else-if="control_type === 'select'"
            v-model="form_data.value_f"
            outlined
            :class="[inputCssClass, 'selection-field']"
            :label="computedLabel"
            :items="choices"
            :menu-props="{ contentClass: 'select-dense-items' }"
            :item-value="choice_value_prop"
            item-text="name"
            :multiple="field.type === 'choice_multi' || field.type === 'user_multi' || field.type === 'client_groups'"
            :error-messages="form_data.value_error || errorMessages"
            @change="value_changed"
        >
            <template v-slot:label>
                <v-tooltip bottom :disabled="!showTooltip">
                    <template v-slot:activator="{ on }">
                        <div ref="tooltipElem" class="text-field-tooltip-label" v-on="on">
                            {{ computedLabel }}
                        </div>
                    </template>
                    <span>{{ computedLabel }} </span>
                </v-tooltip>
            </template>
        </v-select>
        <v-menu
            v-else-if="control_type === 'date'"
            v-model="date_menu"
            lazy
            offset-y
            :close-on-content-click="false"
            :nudge-top="18"
            min-width="280"
        >
            <template v-slot:activator="{ on }">
                <v-text-field
                    :label="computedLabel"
                    readonly
                    :value="form_data.value | format_date"
                    :error-messages="form_data.value_error || errorMessages"
                    outlined
                    :class="inputCssClass"
                    append-icon="mdi-calendar"
                    v-on="on"
                >
                    <template v-slot:label>
                        <v-tooltip bottom :disabled="!showTooltip">
                            <template v-slot:activator="{ on }">
                                <div ref="tooltipElem" class="text-field-tooltip-label" v-on="on">
                                    {{ computedLabel }}
                                </div>
                            </template>
                            <span>{{ computedLabel }} </span>
                        </v-tooltip>
                    </template>
                </v-text-field>
            </template>
            <v-date-picker
                v-model="form_data.value"
                min="1900-01-01"
                color="primary"
                no-title
                :locale="locale"
                :first-day-of-week="firstDayOfWeek"
                @input="value_changed"
            ></v-date-picker>
        </v-menu>
        <v-checkbox
            v-else-if="control_type === 'checkbox'"
            :input-value="computedCheckboxValue"
            :class="[inputCssClass]"
            :label="computedLabel"
            :true-value="1"
            :false-value="0"
            :error-messages="form_data.value_error || errorMessages"
            @change="value_changed"
        ></v-checkbox>
    </div>
</template>

<script>
import Vue from "vue";
import { $t } from "@/i18n";
import { ref, computed, watch, onMounted, onUpdated, onUnmounted } from "@vue/composition-api";
import { formatNumber, cleanNumber, formatMoneyNumber } from "@/plugins/formatting";
import AppModule from "../store/modules/app";
import UserModule from "@/store/modules/user";
import errorDialog from "@/views/ErrorDialog/errorDialog"
import {filterNumber} from "@/helpers/helpers";


export default {
    name: "CustomField",
    props: {
        accountId: {
            type: Number,
            default: 0
        },
        field: {
            type: Object,
            required: true
        },
        value: {
            type: [String, Number, Array],
            default: null
        },
        errorMessages: {
            type: String,
            default: null
        },
        inputCssClass: {
            type: String,
            default: ""
        }
    },
    setup(props, ctx) {
        const { fetchClassifiers, articles } = AppModule();
        const userModule = UserModule();

        const form_data = ref(null);
        const date_menu = ref(false);
        const tooltipElem = ref(null);
        const showTooltip = ref(false);

        const computedLabel = computed(() => {
            let value = props.field.title

            if (props.field.required) {
                value += ' *'
            }

            return value
        })

        const control_type = computed(function() {
            switch (props.field.type) {
                case "price":
                    return "price"
                case "choice_single":
                case "choice_multi":
                case "user_single":
                case "user_multi":
                case "article":
                case "client_groups":
                case "language":
                    return "select"
                case "boolean":
                    return "checkbox"
                case "date":
                    return "date";
                case "text":
                case "integer":
                case "number":
                default:
                    return "text"
            }
        });
        const placeholder = computed(function() {
            switch (props.field.type) {
                case "integer":
                    return "0";
                case "number":
                    return "0";
                default:
                    return "";
            }
        });
        const orig_value = computed(function() {
            return props.value;
        });
        const choices = computed(function() {
            // enum custom choices
            if (control_type.value !== "select") return [];
            if (props.field.type === "choice_single" || props.field.type === "choice_multi" || props.field.type === "client_groups" || props.field.type === "language") {

                // console.log('props.field', props.field)
                if (props.field._data == null || props.field._data.choices === undefined || props.field._data.choices.length === undefined)
                    return [];

                let choices = props.field._data.choices;
                for (let i = 0; i < choices.length; i++) {
                    // console.log(choices[i].name, choices[i].code instanceof String, choices[i].code)
                    choices[i].code = typeof choices[i].code === 'string' ? choices[i].code.toLowerCase() : choices[i].code
                }
                props.field._data.choices = choices
                return props.field._data.choices
            }
            if (props.field.type === "user_single" || props.field.type === "user_multi") {
                return userModule.getVisibleMembersByCompany(props.accountId)
            }
            if (props.field.type === "article") {
                if (!props.accountId) return [];
                return articles.value[props.accountId] !== undefined ? articles.value[props.accountId] : [];
            }
        });
        const choice_value_prop = computed(function() {
            if (props.field.type === "user_single" || props.field.type === "user_multi" || props.field.type === "client_groups") {
                return "id";
            }
            else {
                return "code";
            }
        });
        const locale = computed(function() {
            return Vue.formatting.culture;
        });
        const firstDayOfWeek = computed(function() {
            return Vue.formatting.first_week_day;
        });

        const input_mode = computed(function() {
            if (props.field.type === "number") return "decimal";
            if (props.field.type === "integer") return "numeric";
            return null;
        });

        const load_data = function() {
            load_classifiers();
            form_data.value = {
                value: props.value,
                value_f: "",
                value_error: null
            };

            // user value
            if (props.value != null) {
                if (form_data === "number" || props.field.type === "integer") {
                    form_data.value.value_f = formatNumber(parseFloat(props.value))
                }
                else if (props.field.type === "choice_single" || props.field.type === "language") {
                    form_data.value.value_f = typeof props.value === 'string' ? props.value.toLowerCase() : props.value;
                }
                else if (props.field.type === "choice_multi") {
                    let values = props.value
                    for (let i = 0; i < values.length; i++) {
                        values[i] = typeof values[i] === 'string' ? values[i].toLowerCase() : values[i]
                    }
                    form_data.value.value_f = values
                }
                else {
                    form_data.value.value_f = props.value
                }
            }
        };
        const load_classifiers = function() {
            let types = [];
            if (props.field.type === "article") types.push("articles");

            if (types.length > 0) {
                fetchClassifiers({ types: types, account_id: props.accountId }).catch(ex => errorDialog.showError(ex));
            }
        };

        const value_changed = (function() {
            if (props.field.type === "price") {
                return function(data) {
                    let value = Number(cleanNumber(data));

                    if (isNaN(value)) {
                        form_data.value.value = 0;
                    } else {
                        value = Math.round(parseFloat(value) * 100);
                        form_data.value.value = value / 100;
                    }

                    ctx.emit("change", form_data.value.value);
                };
            } else if (props.field.type === "number" || props.field.type === "integer") {
                return function(data) {
                    form_data.value.value_error = null;

                    if (!data) {
                        form_data.value.value = null;
                        ctx.emit("change", form_data.value.value);
                        return;
                    }

                    let num_str = cleanNumber(data);
                    if (isNaN(num_str) || (props.field.type === "integer" && !/^\d*$/.test(num_str))) {
                        form_data.value.value_error =
                            props.field.type === "integer" ? $t("Must be an integer") : $t("Must be a number");
                        form_data.value.value = null;
                    } else {
                        form_data.value.value = parseFloat(num_str);
                        ctx.emit("change", form_data.value.value);
                    }
                };
            } else if (props.field.type === "date") {
                return function(data) {
                    date_menu.value = false;
                    form_data.value.value_error = null;
                    form_data.value.value = data;
                    ctx.emit("change", form_data.value.value);
                };
            } else {
                return function(data) {
                    form_data.value.value_error = null;
                    form_data.value.value = data;
                    ctx.emit("change", form_data.value.value);
                };
            }
        })();

        watch(orig_value, function(val) {
            if (form_data.value == null) return;
            form_data.value.value = val;
            form_data.value.value_error = null;
            if (val == null) form_data.value.value_f = "";
            else
                form_data.value.value_f =
                    props.field.type === "number" || props.field.type === "integer"
                        ? formatNumber(parseFloat(val))
                        : val;
        });
        const accountIdWatch = computed(() => props.accountId);
        watch(accountIdWatch, (val, prev) => {
            if (prev != null) load_classifiers();
        });

        load_data();

        const computedCheckboxValue = computed(() => {
            if (props.field.type === "boolean"){
                const truthy_values = [true, 'true', '1', 1, 'yes', 'True', 'Yes', 'YES', 'y', 'Y', 'TRUE']
                if (truthy_values.includes(form_data.value.value)) return 1
                else return 0
            }
            return 0
        })

        const calcShowTooltip = () => {
            showTooltip.value = tooltipElem.value.clientWidth < tooltipElem.value.scrollWidth
        }

        onMounted(() => {
            setTimeout(() => {
                calcShowTooltip()
            }, 100)

            window.addEventListener("resize", calcShowTooltip);
        })

        onUnmounted(() => {
            window.removeEventListener("resize", calcShowTooltip)
        })

        return {
            form_data,
            control_type,
            placeholder,
            choices,
            choice_value_prop,
            value_changed,
            date_menu,
            locale,
            input_mode,
            computedCheckboxValue,
            showTooltip,
            tooltipElem,
            computedLabel,
            firstDayOfWeek,
            filterNumber,
            formatMoneyNumber
        };
    }
};
</script>

<style lang="scss" scoped>
::v-deep .selection-field input {
    padding: 0 !important;
    display: none;
}

::v-deep .v-select__selection--comma {
    max-width: 100%;
}
::v-deep .v-input--checkbox .v-input__slot {
    padding-top: 10px;
}

.text-field-tooltip-label {
    overflow: hidden;
    text-overflow: ellipsis;
    pointer-events: all;
}

</style>
